/** Wire payload -> the domain shape the mapper is written against.
 *
 * This is the ONLY layer that knows the API's field names. Keeping it separate is what
 * lets `mappers/` stay a line-by-line port of the Kotlin: when the backend serializer
 * changes, this file changes; when Android changes what it renders, the mapper does.
 */
import { toDomainImage, type DomainImage } from '../images';
import type { Currency } from '../text/format';

export interface ActivityImage {
  id: number;
  image: DomainImage | null;
  caption: string | null;
  videoUrl: string | null;
  sequence: number;
  hasVideo: boolean;
  is360: boolean;
}

export interface ActivityDocument {
  id: number;
  sequence: number;
  name: string;
  link: string | null;
  contentType: string | null;
  /** Ontrip_Document.type — drives the content-type icon. */
  type: number | null;
}

export interface ActivityRating {
  starRating: number;
  reviewCount: number;
  reviewSourceUrl: string | null;
}

export interface ActivityDomain {
  id: number;
  name: string;
  previewText: string | null;
  description: string | null;
  minDuration: number | null;
  maxDuration: number | null;
  images: ActivityImage[];
  documents: ActivityDocument[];
  rating: ActivityRating | null;
  openingHours: unknown[];
  email: string | null;
  phone: string | null;
  address: string | null;
  website: string | null;
  coordinates: { lat: number; lng: number } | null;
  /** The payload carries child ids only — see `childIds`. */
  childIds: number[];
  paymentType: number | null;
  priceType: number | null;
  priceLevel: number | null;
  costMin: number | null;
  costMax: number | null;
  costPrefix: string | null;
  costUnit: string | null;
  currencyId: number | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/** The rating row with the highest review count (a missing count counts as 0); the first of
 *  equals wins, so ties keep the server's order. */
export function pickTopRating(rows: unknown[]): Record<string, unknown> | undefined {
  let top: Record<string, unknown> | undefined;
  let topCount = -Infinity;
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const row = r as Record<string, unknown>;
    const count = num(row.review_count) ?? 0;
    if (count > topCount) {
      top = row;
      topCount = count;
    }
  }
  return top;
}

export function adaptActivity(payload: Record<string, unknown>): ActivityDomain {
  const rawImages = Array.isArray(payload.images) ? payload.images : [];
  const rawDocs = Array.isArray(payload.documents) ? payload.documents : [];
  const rawRatings = Array.isArray(payload.ratings) ? payload.ratings : [];

  const images: ActivityImage[] = rawImages.map((r) => {
    const o = r as Record<string, unknown>;
    const videoUrl = str(o.video_url);
    return {
      id: Number(o.id ?? 0),
      image: toDomainImage(o.image),
      caption: str(o.caption),
      videoUrl,
      sequence: num(o.sequence) ?? 0,
      hasVideo: videoUrl !== null,
      is360: o.is_360_degree === true,
    };
  });

  const documents: ActivityDocument[] = rawDocs.map((r) => {
    const o = r as Record<string, unknown>;
    const doc = (o.document ?? {}) as Record<string, unknown>;
    return {
      id: Number(o.id ?? 0),
      sequence: num(o.sequence) ?? 0,
      name: str(doc.name) ?? '',
      link: str(doc.link),
      contentType: str(doc.content_type),
      type: num(doc.type),
    };
  });

  // `rating` is the source with the most reviews — as iOS shows it (the ratings sorted by
  // review count, descending, the top one). The server lists them by source name, so the
  // first is not the best known (an activity with Google 915 / TripAdvisor 1120 / Yelp 19
  // shows TripAdvisor's 1120). Ties keep the server's order.
  const topRating = pickTopRating(rawRatings);
  const rating: ActivityRating | null = topRating
    ? {
        starRating: num(topRating.star_rating) ?? 0,
        reviewCount: num(topRating.review_count) ?? 0,
        reviewSourceUrl: str(topRating.review_source_url),
      }
    : null;

  const lat = num(payload.latitude);
  const lng = num(payload.longitude);

  return {
    id: Number(payload.id ?? 0),
    name: str(payload.name) ?? '',
    previewText: str(payload.preview_text),
    description: str(payload.description),
    minDuration: num(payload.min_duration),
    maxDuration: num(payload.max_duration),
    images,
    documents,
    rating,
    openingHours: Array.isArray(payload.opening_hours) ? payload.opening_hours : [],
    email: str(payload.email),
    phone: str(payload.phone),
    address: str(payload.address),
    website: str(payload.website),
    coordinates: lat !== null && lng !== null ? { lat, lng } : null,
    childIds: Array.isArray(payload.children) ? (payload.children as number[]) : [],
    paymentType: num(payload.payment_type),
    priceType: num(payload.price_type),
    priceLevel: num(payload.price_level),
    costMin: num(payload.cost_min),
    costMax: num(payload.cost_max),
    costPrefix: str(payload.cost_prefix),
    costUnit: str(payload.cost_unit),
    currencyId: num(payload.currency),
  };
}

/** The payload carries `currency` as an id and no currency table, so the renderer
 *  cannot resolve a symbol. Callers pass whatever they know; null is honest. */
export const NO_CURRENCY: Currency | null = null;
